import axios from "axios";
import { CognitoUser } from "@aws-amplify/auth";
import { Auth } from "aws-amplify";

type RedirectFunction = () => void;

const BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

let user: CognitoUser | null = null;
let logoutRedirect: RedirectFunction | null = null;

export const setUserForApiRequests = (newUser: CognitoUser) => {
  try {
    // Check if the user has a valid session before setting
    if (
      newUser &&
      newUser.getSignInUserSession() &&
      newUser.getSignInUserSession()?.getIdToken()?.getJwtToken()
    ) {
      user = newUser;
    } else {
      console.warn("Attempted to set an unauthenticated user for API requests");
    }
  } catch (error) {
    console.error("Error setting user for API requests:", error);
  }
};

export const setLogoutRedirectForApiResponse = (
  logoutRedirectFunction: RedirectFunction
) => {
  logoutRedirect = logoutRedirectFunction;
};

// Add an interceptor to include the Authorization token in the request header
api.interceptors.request.use(
  (config) => {
    const token = user?.getSignInUserSession()?.getIdToken()?.getJwtToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration and refresh logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check for a 403 (forbidden) error and expired token
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true; // Mark the request as retried to avoid infinite loops

      try {
        // Attempt to refresh the session
        const session = await Auth.currentSession();
        const newAccessToken = session.getIdToken().getJwtToken();

        // Update the token in the user's session and retry the request
        setUserForApiRequests(await Auth.currentAuthenticatedUser());

        // Retry the request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Failed to refresh token:", refreshError);

        // If token refresh failed, redirect to login or handle gracefully
        if (logoutRedirect) {
          logoutRedirect();
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other types of errors or if the token refresh has already been retried
    return Promise.reject(error);
  }
);

export default api;
